import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Alert, Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import api from '../utils/api'; // Import the consolidated API utility
import getSquareConfig from '../config/square';
import './SubscriptionPage.css';

const SubscriptionPage = () => {
  // Get Square configuration from our helper
  const { isProduction, squareAppId, squareLocationId } = getSquareConfig();
  
  // Auth Context
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Subscription Data State
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [paymentConfigError, setPaymentConfigError] = useState(false);
  
  // View State
  const [view, setView] = useState('initial'); // 'initial', 'cancel-confirmation'
  
  // Payment State
  const [billingAddress, setBillingAddress] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  // Log environment information and check Square credentials
  useEffect(() => {
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Using Square credentials for:', isProduction ? 'PRODUCTION' : 'SANDBOX');
    
    // Check if Square credentials are available
    if (!squareAppId || !squareLocationId) {
      console.error('Square payment credentials are missing or invalid!');
      setPaymentConfigError(true);
    } else {
      console.log('Square credentials loaded successfully');
      setPaymentConfigError(false);
    }
  }, [isProduction, squareAppId, squareLocationId]);

  // Load initial data
  useEffect(() => {
    if (auth && auth.vendorId) {
      fetchVendorDetails();
    }
  }, [auth]);

  // API Calls - Using api utility
  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      // Use the api utility with the simplified path
      const response = await api.get(`/subscriptions/${auth.vendorId}`);
      setSubscription(response.data.subscription);
      setLoading(false);
    } catch (err) {
      console.error("Subscription error:", err);
      setError('Failed to load subscription status');
      setLoading(false);
    }
  };

  const fetchVendorDetails = async () => {
    try {
      setLoading(true);
      // Use the api utility with the simplified path
      const response = await api.get(`/vendors/${auth.vendorId}`);
      setVendorData(response.data.vendor);
      
      // After getting vendor details, fetch subscription status
      fetchSubscriptionStatus();
    } catch (err) {
      console.error("Error fetching vendor details:", err);
      setError('Failed to load vendor details. Please try again.');
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setProcessing(true);
      setCancelSuccess(false);
      
      // Use the api utility with the simplified path
      const response = await api.post('/subscriptions/cancel', { 
        vendorId: auth.vendorId 
      });
      
      setCancelSuccess(true);
      setProcessing(false);
      
      // Fetch updated subscription status
      await fetchSubscriptionStatus();
    } catch (err) {
      console.error("Error canceling subscription:", err);
      
      // Check if the error is due to already being canceled
      const errorDetails = err.response?.data?.details || '';
      if (errorDetails.includes('pending cancel date') || errorDetails.includes('already canceled')) {
        setCancelSuccess(true);
        await fetchSubscriptionStatus();
      } else {
        setError('Failed to cancel subscription: ' + (err.response?.data?.details || err.message));
      }
      setProcessing(false);
    }
  };

  const handleCardTokenizeResponseReceived = async (token) => {
    try {
      setProcessing(true);
      
      // Create complete billing details
      const billingDetails = {
        addressLine1: billingAddress,
        addressLine2: billingAddressLine2, // Optional
        city: billingCity,
        state: billingState,
        postalCode: postalCode,
        country: 'US'
      };
      
      console.log('Submitting payment with billing details:', {
        ...billingDetails,
        // Don't log full token for security
        tokenReceived: !!token,
        tokenType: token ? typeof token : 'none'
      });
      
      // Use the api utility with the simplified path
      const response = await api.post(
        '/subscriptions/process-payment',      
        {
          vendorId: auth.vendorId,
          sourceId: token.token,
          billingDetails: billingDetails
        }
      );
      
      setSuccess(true);
      setProcessing(false);
      await fetchSubscriptionStatus();
      setView('initial');
    } catch (err) {
      console.error('Payment processing error:', err);
      const errorMessage = err.response?.data?.details || err.response?.data?.message || err.message;
      
      // Even if there was an error, check if subscription was actually created
      try {
        await fetchSubscriptionStatus();
        const subscriptionExists = subscription && 
                                (subscription.status === 'ACTIVE' || 
                                 subscription.status === 'PENDING');
        
        if (subscriptionExists) {
          setSuccess(true);
          setProcessing(false);
          setView('initial');
          return;
        }
      } catch (checkErr) {
        console.error('Error checking subscription status after payment error:', checkErr);
      }
      
      setError('Payment processing failed: ' + errorMessage);
      setProcessing(false);
    }
  };

  // Event Handlers
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    
    switch(id) {
      case 'billingAddress':
        setBillingAddress(value);
        break;
      case 'billingAddressLine2':
        setBillingAddressLine2(value);
        break;
      case 'billingCity':
        setBillingCity(value);
        break;
      case 'billingState':
        setBillingState(value);
        break;
      case 'postalCode':
        setPostalCode(value);
        break;
      default:
        break;
    }
  };

  const handleBackToDashboard = () => {
    navigate('/vendor-dashboard');
  };

  // Helper Functions
  const isFormComplete = () => {
    return (
      billingAddress.trim() !== '' &&
      billingCity.trim() !== '' &&
      billingState.trim() !== '' &&
      postalCode.trim() !== ''
    );
  };

  // Get display status - handle canceled subscriptions with pending cancel dates
  const getDisplayStatus = () => {
    if (!subscription) return null;
    
    if (subscription.canceled_date || subscription.status === 'CANCELED' || cancelSuccess) {
      return 'CANCELED';
    }
    return subscription.status;
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'CANCELED': return 'danger';
      case 'PAUSED': return 'info';
      default: return 'secondary';
    }
  };

  // Render Functions
  const renderCancellationSuccess = () => {
    if (!cancelSuccess) return null;
    
    return (
      <Alert variant="success">
        <div className="subscription-feature-item">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Subscription successfully canceled</span>
        </div>
        <p>
          Your subscription has been canceled. You will have access to all features until the end of your current billing period
          {subscription && subscription.canceled_date 
            ? ` (${new Date(subscription.canceled_date).toLocaleDateString()})`
            : '.'}
        </p>
      </Alert>
    );
  };
  
  const renderBillingForm = () => {
    return (
      <div className="billing-form mb-4">
        <h5>Billing Address</h5>
        <Form.Group className="mb-3">
          <Form.Label>Street Address</Form.Label>
          <Form.Control
            type="text"
            id="billingAddress"
            value={billingAddress}
            onChange={handleInputChange}
            placeholder="123 Main St"
            required
          />
        </Form.Group>
        
        <Form.Group className="mb-3">
          <Form.Label>Address Line 2 (Optional)</Form.Label>
          <Form.Control
            type="text"
            id="billingAddressLine2"
            value={billingAddressLine2}
            onChange={handleInputChange}
            placeholder="Apt, Suite, Unit, etc."
          />
        </Form.Group>
        
        <Row>
          <Col md={5}>
            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                id="billingCity"
                value={billingCity}
                onChange={handleInputChange}
                placeholder="San Francisco"
                required
              />
            </Form.Group>
          </Col>
          
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                id="billingState"
                value={billingState}
                onChange={handleInputChange}
                placeholder="CA"
                required
              />
            </Form.Group>
          </Col>
          
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Postal Code</Form.Label>
              <Form.Control
                type="text"
                id="postalCode"
                value={postalCode}
                onChange={handleInputChange}
                placeholder="94103"
                required
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
    );
  };

  const renderPaymentForm = () => {
    // Use vendor data for payment form if available
    const firstName = vendorData?.firstName || 'Vendor';
    const lastName = vendorData?.lastName || 'Account';
    const email = vendorData?.email || '';
    
    // Check if required credentials are available
    if (paymentConfigError || !squareAppId || !squareLocationId) {
      return (
        <Alert variant="danger">
          <h5>Payment Processing Unavailable</h5>
          <p>We're experiencing technical difficulties with our payment processor. Please try again later or contact support.</p>
          <p className="mb-0">Error: Missing payment configuration.</p>
        </Alert>
      );
    }
    
    console.log('Rendering Square Payment Form with:', {
      environment: isProduction ? 'PRODUCTION' : 'SANDBOX',
      appIdExists: !!squareAppId,
      locationIdExists: !!squareLocationId
    });

    return (
      <div className="payment-form-container">
        {renderBillingForm()}
        
        <div className="credit-card-wrapper">
          <PaymentForm
            applicationId={squareAppId}
            locationId={squareLocationId}
            cardTokenizeResponseReceived={handleCardTokenizeResponseReceived}
            createVerificationDetails={() => ({
              amount: '79.00',
              currencyCode: 'USD',
              intent: 'STORE',
              billingContact: {
                familyName: lastName,
                givenName: firstName,
                email: email,
                addressLines: [billingAddress, billingAddressLine2].filter(Boolean),
                city: billingCity,
                state: billingState,
                country: 'US',
                postalCode: postalCode
              }
            })}
          >
            <CreditCard />
          </PaymentForm>
        </div>
      </div>
    );
  };

  // Main page content based on view and subscription status
  const renderContent = () => {
    if (loading) {
      return (
        <Card className="text-center p-4">
          <Spinner animation="border" />
          <p className="mt-3">Loading subscription details...</p>
        </Card>
      );
    }

    if (error) {
      return (
        <Card className="p-4">
          <Alert variant="danger">
            <p>{error}</p>
            <Button 
              onClick={() => {
                setError(null);
                setLoading(true);
                setCancelSuccess(false);
                setView('initial');
                if (auth && auth.vendorId) {
                  fetchVendorDetails();
                }
              }}
              variant="primary"
              className="mt-2"
            >
              Try Again
            </Button>
          </Alert>
        </Card>
      );
    }

    if (success) {
      return (
        <Card className="p-4">
          <Alert variant="success">
            <div className="subscription-feature-item">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">Subscription Activated!</span>
            </div>
            <p>
              Your subscription has been successfully activated. Thank you for subscribing to Weedhurry!
            </p>
            <Button 
              onClick={() => {
                setSuccess(false);
                setLoading(true);
                setView('initial');
                fetchSubscriptionStatus();
              }}
              variant="primary"
              className="mt-3"
            >
              View Subscription Status
            </Button>
          </Alert>
        </Card>
      );
    }

    // Render Cancellation Confirmation screen
    if (view === 'cancel-confirmation') {
      return (
        <Card className="subscription-card">
          <Card.Header>
            <h3>Cancel Subscription</h3>
          </Card.Header>
          <Card.Body>
            {cancelSuccess ? (
              <Alert variant="success">
                <div className="subscription-feature-item">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Subscription successfully canceled</span>
                </div>
                <p>
                  Your subscription has been canceled. You will have access to all features until the end of your current billing period
                  {subscription && subscription.canceled_date 
                    ? ` (${new Date(subscription.canceled_date).toLocaleDateString()})`
                    : '.'}
                </p>
              </Alert>
            ) : (
              <>
                <Alert variant="warning" className="mb-4">
                  <h5 className="mb-2">Are you sure you want to cancel your subscription?</h5>
                  <p>
                    If you cancel, you will still have access until the end of your current billing period. 
                    After that, you will lose access to Weedhurry platform features.
                  </p>
                </Alert>
                
                <div className="d-flex justify-content-between">
                  <Button 
                    onClick={() => setView('initial')}
                    variant="secondary"
                    disabled={processing}
                  >
                    No, Keep Subscription
                  </Button>
                  <Button 
                    onClick={handleCancelSubscription}
                    variant="danger"
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <Spinner animation="border" size="sm" className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      'Yes, Cancel Subscription'
                    )}
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
          <Card.Footer>
            <Button 
              variant="primary" 
              onClick={() => {
                setView('initial');
                if (cancelSuccess) {
                  // Refresh subscription data
                  setLoading(true);
                  fetchSubscriptionStatus();
                }
              }}
            >
              {cancelSuccess ? 'View Updated Status' : 'Back to Subscription'}
            </Button>
          </Card.Footer>
        </Card>
      );
    }

    // If no subscription, show subscription required content with payment form directly included
    if (!subscription) {
      return (
        <Card className="subscription-card">
          <Card.Header>
            <h3>Subscription Required</h3>
          </Card.Header>
          <Card.Body>
            <p className="mb-4">You need to subscribe to continue using Weedhurry's platform for your dispensary.</p>
            <div className="subscription-features">
              <h4 className="mb-3">Weedhurry Vendor Plan - $79/month</h4>
              <div className="subscription-feature-item">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Full access to Weedhurry platform</span>
              </div>
              <div className="subscription-feature-item">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Unlimited product listings</span>
              </div>
              <div className="subscription-feature-item">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Order management & analytics</span>
              </div>
              <div className="subscription-feature-item">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Customer support</span>
              </div>
            </div>
            
            <hr className="my-4" />
            
            <h4 className="mb-3">Enter Payment Information</h4>
            {renderPaymentForm()}
            
            <Button 
              type="submit"
              disabled={!isFormComplete() || processing || paymentConfigError}
              variant="success"
              className="d-block w-100 mt-4"
              form="square-payment-form"
            >
              {processing ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Processing...
                </>
              ) : (
                'Subscribe Now'
              )}
            </Button>
          </Card.Body>
          <Card.Footer>
            <Button variant="secondary" onClick={handleBackToDashboard}>
              Back to Dashboard
            </Button>
          </Card.Footer>
        </Card>
      );
    }

    // If there is an active subscription, show the status with cancel option
    const displayStatus = getDisplayStatus();
    const statusClass = 
      displayStatus === 'ACTIVE' ? 'status-badge-active' :
      displayStatus === 'CANCELED' ? 'status-badge-canceled' : 
      'status-badge-pending';
    
    return (
      <Card className="subscription-card">
        <Card.Header>
          <h3>Subscription Status</h3>
        </Card.Header>
        <Card.Body>
          {/* Show cancellation success message if applicable */}
          {renderCancellationSuccess()}
          
          <div className="mb-4">
            <div className="status-item">
              <span>Plan:</span>
              <span>Weedhurry Vendor</span>
            </div>
            <div className="status-item">
              <span>Price:</span>
              <span>$79/month</span>
            </div>
            <div className="status-item">
              <span>Status:</span>
              <span>
                <span className={`status-badge ${statusClass}`}>
                  {displayStatus}
                </span>
              </span>
            </div>
            {subscription.start_date && (
              <div className="status-item">
                <span>Start Date:</span>
                <span>{new Date(subscription.start_date).toLocaleDateString()}</span>
              </div>
            )}
            
            {/* Show cancellation date if available */}
            {subscription.canceled_date && (
              <div className="status-item">
                <span>Access Until:</span>
                <span>{new Date(subscription.canceled_date).toLocaleDateString()}</span>
              </div>
            )}
            
            {/* Show next billing date if available */}
            {subscription.billing_anchor && !subscription.canceled_date && (
              <div className="status-item">
                <span>Next Billing:</span>
                <span>{new Date(subscription.billing_anchor).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          
          {/* Conditional rendering based on subscription status */}
          {displayStatus === 'ACTIVE' && !cancelSuccess && (
            <Button 
              onClick={() => setView('cancel-confirmation')}
              variant="danger"
              className="d-block w-100 mb-3"
              disabled={processing}
            >
              Cancel Subscription
            </Button>
          )}
          
          {(displayStatus === 'CANCELED' || cancelSuccess) && (
            <div className="mb-3">
              <p className="mb-3">
                Your subscription has been canceled and will end on {subscription.canceled_date ? new Date(subscription.canceled_date).toLocaleDateString() : 'the end of your billing period'}.
              </p>
              
              <h4 className="mb-3">Reactivate Your Subscription</h4>
              {renderPaymentForm()}
              
              <Button 
                type="submit"
                disabled={!isFormComplete() || processing || paymentConfigError}
                variant="success"
                className="d-block w-100 mt-4"
                form="square-payment-form"
              >
                {processing ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Processing...
                  </>
                ) : (
                  'Reactivate Subscription'
                )}
              </Button>
            </div>
          )}
          
          {displayStatus !== 'ACTIVE' && displayStatus !== 'CANCELED' && !cancelSuccess && (
            <div className="mb-3">
              <h4 className="mb-3">Update Payment Method</h4>
              {renderPaymentForm()}
              
              <Button 
                type="submit"
                disabled={!isFormComplete() || processing || paymentConfigError}
                variant="success"
                className="d-block w-100 mt-4"
                form="square-payment-form"
              >
                {processing ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Processing...
                  </>
                ) : (
                  'Update Payment Method'
                )}
              </Button>
            </div>
          )}
        </Card.Body>
        <Card.Footer>
          <Button variant="secondary" onClick={handleBackToDashboard}>
            Back to Dashboard
          </Button>
        </Card.Footer>
      </Card>
    );
  };

  return (
    <Container className="subscription-page-container mt-5">
      <h2 className="text-center mb-4">Manage Your Subscription</h2>
      {renderContent()}
    </Container>
  );
};

export default SubscriptionPage;